/* eslint-disable react/no-unstable-nested-components */
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Loader2,
  FileText,
  CalendarIcon,
  Download,
  Users,
  TrendingUp,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  Search,
  Target,
  Clock,
  Percent,
  Activity,
  PieChart as PieChartIcon,
  BarChart2,
  FileDown,
  Trophy,
  Medal,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
import { cn } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Funnel,
  FunnelChart,
  LabelList,
} from 'recharts';

const STAGE_OPTIONS = [
  { value: 'all', label: 'All Stages' },
  { value: 'new_lead', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'application_submitted', label: 'Application Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'offer_selected', label: 'Offer Selected' },
  { value: 'funding_pending', label: 'Funding Pending' },
  { value: 'customer_funded', label: 'Customer Funded' },
  { value: 'closed_declined', label: 'Closed / Declined' },
];

const STAGE_COLORS = {
  new_lead: '#3b82f6',
  contacted: '#06b6d4',
  qualifying: '#8b5cf6',
  application_submitted: '#a855f7',
  under_review: '#f59e0b',
  offer_selected: '#10b981',
  funding_pending: '#f97316',
  customer_funded: '#22c55e',
  closed_declined: '#6b7280',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

// Consistent color mapping function - ensures same color for same name
const getConsistentColor = (name, index = 0) => {
  // Simple hash function to generate consistent index based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  const colorIndex = Math.abs(hash) % PIE_COLORS.length;
  return PIE_COLORS[colorIndex];
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (!value && value !== 0) return '-';
  return `${value.toFixed(1)}%`;
};

const ReportsPage = () => {
  useDocumentTitle('Reports');
  const navigate = useNavigate();
  const { user, isAdmin, isManager, isInternal } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [report, setReport] = useState(null);
  const [tableSearch, setTableSearch] = useState('');

  const [filters, setFilters] = useState({
    company_id: 'all',
    director_name: '',
    sales_rep_id: 'all',
    ptc_rep_id: 'all',
    deal_stage: 'all',
    start_date: null,
    end_date: null,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
  });

  const [sorting, setSorting] = useState({
    sort_by: 'updated_at',
    sort_order: 'desc',
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const promises = [
          api.get('/companies'),
          api.get('/users'),
          api.get('/reports/sales-reps'),
        ];
        const [companiesRes, usersRes, salesRepsRes] = await Promise.all(promises);
        setCompanies(companiesRes.data);
        setUsers(usersRes.data);
        setSalesReps(salesRepsRes.data.sales_reps || []);
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      }
    };
    fetchMeta();
  }, []);

  // Fetch leaderboard when date filters change
  const fetchLeaderboard = async () => {
    try {
      let url = '/reports/sales-leaderboard';
      const params = [];
      if (filters.start_date) {
        params.push(`start_date=${filters.start_date.toISOString()}`);
      }
      if (filters.end_date) {
        params.push(`end_date=${filters.end_date.toISOString()}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      const response = await api.get(url);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [filters.start_date, filters.end_date]);

  const buildPayload = (page = pagination.page) => {
    const payload = {
      page,
      per_page: pagination.per_page,
      sort_by: sorting.sort_by,
      sort_order: sorting.sort_order,
    };
    if (filters.company_id !== 'all') payload.company_id = filters.company_id;
    if (filters.director_name) payload.director_name = filters.director_name;
    if (filters.sales_rep_id !== 'all') payload.sales_rep_name = filters.sales_rep_id;
    if (filters.ptc_rep_id !== 'all') payload.ptc_rep_id = filters.ptc_rep_id;
    if (filters.deal_stage !== 'all') payload.deal_stage = filters.deal_stage;
    if (filters.start_date) payload.start_date = filters.start_date.toISOString();
    if (filters.end_date) payload.end_date = filters.end_date.toISOString();
    return payload;
  };

  const generateReport = async (newPage = pagination.page) => {
    setLoading(true);
    try {
      const payload = buildPayload(newPage);
      const response = await api.post('/reports/generate', payload);
      setReport(response.data);
      setPagination((prev) => ({ ...prev, page: newPage }));
      if (newPage === 1) {
        toast.success('Report generated');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const newOrder = sorting.sort_by === column && sorting.sort_order === 'desc' ? 'asc' : 'desc';
    setSorting({ sort_by: column, sort_order: newOrder });
    if (report) {
      setTimeout(() => generateReport(1), 0);
    }
  };

  const handleExport = async (exportFormat) => {
    setExporting(exportFormat);
    try {
      const payload = buildPayload(1);
      delete payload.page;
      delete payload.per_page;

      const response = await api.post(`/reports/export?format=${exportFormat}`, payload, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentType = response.headers['content-type'];
      let filename = `ptc_report_${format(new Date(), 'yyyyMMdd')}`;
      if (exportFormat === 'csv') filename += '.csv';
      else if (exportFormat === 'xlsx') filename += '.xlsx';
      else if (exportFormat === 'pdf') filename += '.pdf';

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported to ${exportFormat.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(null);
    }
  };

  // Filter table records by local search
  const filteredRecords = useMemo(() => {
    if (!report?.records || !tableSearch) return report?.records || [];
    const search = tableSearch.toLowerCase();
    return report.records.filter(
      (r) =>
        r.full_name?.toLowerCase().includes(search) ||
        r.email?.toLowerCase().includes(search) ||
        r.company_name?.toLowerCase().includes(search) ||
        r.sales_rep_name?.toLowerCase().includes(search) ||
        r.director_name?.toLowerCase().includes(search)
    );
  }, [report?.records, tableSearch]);

  // Get users filtered by company selection
  const companyUsers = useMemo(() => {
    if (filters.company_id === 'all') return users;
    return users.filter((u) => u.company_id === filters.company_id);
  }, [users, filters.company_id]);

  // Get PTC reps (internal users)
  const ptcReps = useMemo(() => {
    return users.filter((u) => u.company_type === 'internal');
  }, [users]);

  // Directors in selected company
  const directors = useMemo(() => {
    return companyUsers.filter((u) =>
      ['company_director', 'ptc_director'].includes(u.role)
    );
  }, [companyUsers]);

  // Prepare chart data
  const stageChartData = useMemo(() => {
    if (!report?.by_stage) return [];
    return Object.entries(report.by_stage).map(([stage, count]) => ({
      stage: stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      count,
      fill: STAGE_COLORS[stage] || '#6b7280',
    }));
  }, [report?.by_stage]);

  // Dynamic chart data - shows "Deals by Rep" for external users, "Deals by Company" for internal
  const dynamicChartData = useMemo(() => {
    // For external partner users: show deals by sales rep
    // For internal PTC users: show deals by company
    if (!report) return [];
    
    if (report.is_internal_user) {
      // Internal users see "Deals by Company"
      if (!report.by_company) return [];
      return report.by_company.map((c) => ({
        name: c.name,
        value: c.count,
        fill: getConsistentColor(c.name),
      }));
    } else {
      // External users see "Deals by Rep"
      if (!report.by_sales_rep) return [];
      return report.by_sales_rep.map((r) => ({
        name: r.name,
        value: r.count,
        fill: getConsistentColor(r.name),
      }));
    }
  }, [report?.by_company, report?.by_sales_rep, report?.is_internal_user]);

  // Chart title based on user role
  const dynamicChartTitle = useMemo(() => {
    if (!report) return 'Deals by Company';
    return report.is_internal_user ? 'Deals by Company' : 'Deals by Rep';
  }, [report?.is_internal_user]);

  const companyChartData = useMemo(() => {
    if (!report?.by_company) return [];
    return report.by_company.map((c, i) => ({
      name: c.name,
      value: c.count,
      fill: getConsistentColor(c.name),
    }));
  }, [report?.by_company]);

  const monthlyTrendsData = useMemo(() => {
    if (!report?.monthly_trends) return [];
    return report.monthly_trends.map((m) => ({
      month: m.month,
      'New Deals': m.new_deals,
      Funded: m.funded,
    }));
  }, [report?.monthly_trends]);

  const funnelData = useMemo(() => {
    if (!report?.conversion_funnel) return [];
    return report.conversion_funnel.filter((f) => f.stage !== 'closed_declined');
  }, [report?.conversion_funnel]);

  const SortHeader = ({ column, label }) => (
    <th
      className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sorting.sort_by === column ? (
          sorting.sort_order === 'desc' ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUp className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 text-slate-300" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Generate and analyze deal reports</p>
        </div>
        {report && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={!!exporting}
              data-testid="export-csv-btn"
            >
              {exporting === 'csv' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-1" />
              )}
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('xlsx')}
              disabled={!!exporting}
              data-testid="export-xlsx-btn"
            >
              {exporting === 'xlsx' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-1" />
              )}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={!!exporting}
              data-testid="export-pdf-btn"
            >
              {exporting === 'pdf' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              PDF
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Company Filter - only for internal users */}
            {isInternal() && (
              <div>
                <Label>Company</Label>
                <Select
                  value={filters.company_id}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      company_id: value,
                      director_id: 'all',
                      sales_rep_id: 'all',
                    })
                  }
                >
                  <SelectTrigger data-testid="report-company-filter">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies
                      .filter((c) => c.company_type === 'external')
                      .map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Director Filter - Text Input */}
            {(isInternal() || isManager()) && (
              <div>
                <Label>Director</Label>
                <Input
                  placeholder="Search by director name..."
                  value={filters.director_name}
                  onChange={(e) => setFilters({ ...filters, director_name: e.target.value })}
                  data-testid="report-director-filter"
                />
              </div>
            )}

            {/* Sales Rep Filter - Dropdown with company reps */}
            <div>
              <Label>Sales Rep</Label>
              <Select
                value={filters.sales_rep_id}
                onValueChange={(value) => setFilters({ ...filters, sales_rep_id: value })}
              >
                <SelectTrigger data-testid="report-sales-rep-filter">
                  <SelectValue placeholder="All Sales Reps" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sales Reps</SelectItem>
                  {salesReps.map((rep) => (
                    <SelectItem key={rep} value={rep}>
                      {rep}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* PTC Rep Filter - only for internal users */}
            {isInternal() && (
              <div>
                <Label>PTC Rep</Label>
                <Select
                  value={filters.ptc_rep_id}
                  onValueChange={(value) => setFilters({ ...filters, ptc_rep_id: value })}
                >
                  <SelectTrigger data-testid="report-ptc-rep-filter">
                    <SelectValue placeholder="All PTC Reps" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All PTC Reps</SelectItem>
                    {ptcReps.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Deal Stage Filter */}
            <div>
              <Label>Deal Stage</Label>
              <Select
                value={filters.deal_stage}
                onValueChange={(value) => setFilters({ ...filters, deal_stage: value })}
              >
                <SelectTrigger data-testid="report-stage-filter">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((stage) => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.start_date && 'text-muted-foreground'
                    )}
                    data-testid="report-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.start_date ? format(filters.start_date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.start_date}
                    onSelect={(date) => setFilters({ ...filters, start_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div>
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.end_date && 'text-muted-foreground'
                    )}
                    data-testid="report-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.end_date ? format(filters.end_date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.end_date}
                    onSelect={(date) => setFilters({ ...filters, end_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Generate Button */}
            <div className="flex items-end">
              <Button
                onClick={() => generateReport(1)}
                className="w-full bg-accent hover:bg-gold-dark"
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <div className="space-y-4" data-testid="report-results">
          {/* Summary Cards - Row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Clients</p>
                    <p className="text-xl font-bold text-primary" data-testid="metric-total-clients">
                      {report.summary.total_clients}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <DollarSign className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Deal Value</p>
                    <p className="text-xl font-bold text-primary" data-testid="metric-total-value">
                      {formatCurrency(report.summary.total_deal_value)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Funded Deals</p>
                    <p className="text-xl font-bold text-green-600" data-testid="metric-funded-count">
                      {report.summary.funded_deals_count}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <TrendingUp className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Avg Deal Size</p>
                    <p className="text-xl font-bold text-amber-600" data-testid="metric-avg-deal">
                      {formatCurrency(report.summary.average_deal_size)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Activity className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">In Pipeline</p>
                    <p className="text-xl font-bold text-purple-600" data-testid="metric-pipeline">
                      {report.summary.deals_in_pipeline}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Settled This Month</p>
                    <p className="text-xl font-bold text-emerald-600" data-testid="metric-funded-month">
                      {report.summary.deals_funded_this_month}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards - Row 2 (Conversion Metrics) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100">
                    <Percent className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Approval Rate</p>
                    <p className="text-xl font-bold text-cyan-600" data-testid="metric-approval-rate">
                      {formatPercent(report.summary.approval_rate)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-teal-100">
                    <Percent className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Funding Rate</p>
                    <p className="text-xl font-bold text-teal-600" data-testid="metric-funding-rate">
                      {formatPercent(report.summary.funding_rate)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Avg Time to Fund</p>
                    <p className="text-xl font-bold text-indigo-600" data-testid="metric-time-to-fund">
                      {report.summary.average_time_to_fund
                        ? `${report.summary.average_time_to_fund} days`
                        : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Waiting on ACH</p>
                    <p className="text-xl font-bold text-orange-600" data-testid="metric-ach-waiting">
                      {report.summary.deals_waiting_on_ach}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Deals by Stage - Bar Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <BarChart2 className="h-5 w-5 text-slate-500" />
                  Deals by Stage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stageChartData} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="stage"
                        type="category"
                        width={120}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value) => [value, 'Deals']}
                        contentStyle={{ borderRadius: 8 }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {stageChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Deals by Company/Rep - Dynamic Donut Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5 text-slate-500" />
                  {dynamicChartTitle}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {dynamicChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={dynamicChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name.length > 10 ? name.slice(0, 10) + '...' : name} (${(percent * 100).toFixed(0)}%)`
                          }
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {dynamicChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value, 'Deals']}
                          contentStyle={{ borderRadius: 8 }}
                        />
                        {/* Center Label showing Total Volume */}
                        <text
                          x="50%"
                          y="45%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-slate-500"
                          style={{ fontSize: '12px' }}
                        >
                          Total Volume
                        </text>
                        <text
                          x="50%"
                          y="55%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-slate-900"
                          style={{ fontSize: '16px', fontWeight: 'bold' }}
                        >
                          {formatCurrency(report?.summary?.total_deal_value || 0)}
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Monthly Trends - Line Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-slate-500" />
                  Monthly Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {monthlyTrendsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={monthlyTrendsData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip contentStyle={{ borderRadius: 8 }} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="New Deals"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="Funded"
                          stroke="#22c55e"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No trend data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Conversion Funnel */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-slate-500" />
                  Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {funnelData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <FunnelChart>
                        <Tooltip
                          formatter={(value, name) => [value, 'Deals']}
                          contentStyle={{ borderRadius: 8 }}
                        />
                        <Funnel
                          dataKey="count"
                          data={funnelData}
                          isAnimationActive
                        >
                          <LabelList
                            position="center"
                            fill="#fff"
                            stroke="none"
                            dataKey="label"
                            fontSize={11}
                          />
                          {funnelData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={STAGE_COLORS[entry.stage] || PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </Funnel>
                      </FunnelChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No funnel data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sales Leaderboard */}
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200" data-testid="sales-leaderboard">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-amber-800">
                  <Trophy className="h-5 w-5" />
                  Top Sales Reps
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {leaderboard.slice(0, 10).map((entry) => (
                      <div
                        key={entry.rank}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          entry.rank === 1 ? 'bg-amber-200/50' :
                          entry.rank === 2 ? 'bg-slate-200/50' :
                          entry.rank === 3 ? 'bg-orange-200/50' :
                          'bg-white/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                            entry.rank === 1 ? 'bg-amber-400 text-amber-900' :
                            entry.rank === 2 ? 'bg-slate-400 text-slate-900' :
                            entry.rank === 3 ? 'bg-orange-400 text-orange-900' :
                            'bg-slate-200 text-slate-700'
                          }`}>
                            {entry.rank <= 3 ? (
                              <Medal className="h-4 w-4" />
                            ) : (
                              entry.rank
                            )}
                          </span>
                          <span className="font-medium text-slate-800">{entry.sales_rep_name}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-slate-900">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(entry.funded_volume)}
                          </div>
                          <div className="text-xs text-slate-500">{entry.deal_count} deal{entry.deal_count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-amber-700/60">
                    No sales data available for the selected period
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Records Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Detailed Records</CardTitle>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search results..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    className="pl-10 w-64"
                    data-testid="table-search"
                  />
                </div>
                <Select
                  value={String(pagination.per_page)}
                  onValueChange={(value) => {
                    setPagination((prev) => ({ ...prev, per_page: parseInt(value), page: 1 }));
                    setTimeout(() => generateReport(1), 0);
                  }}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="25">25 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                    <SelectItem value="100">100 / page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="report-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <SortHeader column="full_name" label="Client" />
                      {isInternal() && (
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">
                          Company
                        </th>
                      )}
                      <SortHeader column="deal_stage" label="Stage" />
                      <SortHeader column="deal_amount" label="Deal Amt" />
                      <SortHeader column="approved_amount" label="Approved" />
                      <SortHeader column="funded_amount" label="Funded" />
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">
                        Director
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">
                        Sales Rep
                      </th>
                      {isInternal() && (
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">
                          PTC Rep
                        </th>
                      )}
                      <SortHeader column="updated_at" label="Updated" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isInternal() ? 10 : 8}
                          className="py-12 text-center text-slate-500"
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      filteredRecords.map((record) => (
                        <tr
                          key={record.id}
                          className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/clients/${record.id}`)}
                          data-testid={`report-row-${record.id}`}
                        >
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-800">{record.full_name}</p>
                              <p className="text-xs text-slate-500">{record.email}</p>
                            </div>
                          </td>
                          {isInternal() && (
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {record.company_name}
                            </td>
                          )}
                          <td className="py-3 px-4">
                            <Badge
                              className="text-xs"
                              style={{
                                backgroundColor: `${STAGE_COLORS[record.deal_stage] || '#6b7280'}20`,
                                color: STAGE_COLORS[record.deal_stage] || '#6b7280',
                              }}
                            >
                              {(record.deal_stage || 'new_lead').replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-slate-800">
                            {formatCurrency(record.deal_amount)}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-green-600">
                            {formatCurrency(record.approved_amount)}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-emerald-600">
                            {formatCurrency(record.funded_amount)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {record.director_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {record.sales_rep_name || '-'}
                          </td>
                          {isInternal() && (
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {record.ptc_rep_name || '-'}
                            </td>
                          )}
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {record.updated_at
                              ? formatET(record.updated_at, 'MMM d, yyyy')
                              : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {report.pagination && report.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {(report.pagination.page - 1) * report.pagination.per_page + 1} to{' '}
                    {Math.min(
                      report.pagination.page * report.pagination.per_page,
                      report.pagination.total_count
                    )}{' '}
                    of {report.pagination.total_count} results
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(1)}
                      disabled={report.pagination.page === 1 || loading}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(report.pagination.page - 1)}
                      disabled={report.pagination.page === 1 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {report.pagination.page} of {report.pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(report.pagination.page + 1)}
                      disabled={report.pagination.page === report.pagination.total_pages || loading}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(report.pagination.total_pages)}
                      disabled={report.pagination.page === report.pagination.total_pages || loading}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generated Time */}
          <p className="text-sm text-slate-400 text-center">
            Report generated at {formatET(report.generated_at, 'PPpp')}
          </p>
        </div>
      )}

      {!report && !loading && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Configure filters and generate a report</p>
            <p className="text-sm text-slate-400 mt-1">
              The report will include summary metrics, charts, and a detailed table of all
              matching deals
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
